import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useState,
} from 'react';
import {
  TextField,
  Button,
  Stack,
  Box,
  Paper,
  Typography,
  useMediaQuery,
  MenuItem,
  MenuList,
  InputAdornment,
  IconButton,
} from '@mui/material';
import useBludataLogoThemed from '../utility_objects/const/imageUrls';
import json2mq from 'json2mq';
import EmptyPage from '../utility_objects/EmptyPage';
import useLogoutMSAL from './useLogoutMSAL';
import { getWorkplaces } from './api/GetWorkplaces';
import { BluLoader, BluTitle } from '@bludata/components';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { getSessions, useSessions } from './api/GetSessions';
import dayjs from 'dayjs';

interface Props {
  onSessionSet: (sessionRef: string | undefined) => Promise<void>;
}

function LoginChooseSessionPage(props: Props) {
  const { logoFocusUrl } = useBludataLogoThemed();
  const [sessions, setSessions] = useState([]);

  const [sessionRef, setSessionRef] = useState('');
  const { logout } = useLogoutMSAL();
  const { onSessionSet } = props;
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const matchesSmall = useMediaQuery(
    json2mq({
      maxWidth: 1024,
    }),
  );

  const matchesMedium = useMediaQuery(
    json2mq({
      maxWidth: 1500,
    }),
  );

  const handleSubmit = useCallback(() => {
    // onPdvSet(workplaceRef);
    onSessionSet(sessionRef);
  }, [sessionRef]);

  useEffect(() => {
    const getAsyncWorkplaces = async () => {
      setIsLoading(true);
      const result = await getSessions();
      console.log('Fetched workplaces:', result);
      setSessions(result);
      setIsLoading(false);
    };
    getAsyncWorkplaces();
  }, []);

  const getValues = useCallback(() => {
    return (sessions ?? []).filter((item) =>
      item.userName.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [searchValue, sessions]);

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && sessionRef !== '') {
      handleSubmit();
    }
  };

  const onAnnullaClick = () => {
    logout(true, 'explicit');
  };

  return (
    <EmptyPage>
      <BluLoader open={isLoading} />
      <Paper
        elevation={0}
        sx={{
          width: 400,
          p: '40px 30px',
          zIndex: 9999,
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
          position: matchesSmall ? undefined : 'absolute',
          right: matchesMedium ? '10%' : '20%',
        }}
      >
        <Stack flexDirection="column">
          <Stack flexDirection="row" justifyContent="center">
            <Box
              sx={{
                margin: '10px 0px',
                marginBottom: '40px',
                height: 32,
                width: 190,
                backgroundImage: `url(${logoFocusUrl})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'auto 100%',
                backgroundPosition: 'center',
              }}
            />
          </Stack>
          <Stack flexDirection="column" spacing={2}>
            <Typography fontSize="16px" textAlign="center">
              Hai raggiunto il limite di sessioni attive consentite dalla tua
              licenza.
              <br />
              Seleziona una sessione da disattivare per poter accedere a Focus
              Cloud.
            </Typography>

            <Stack flexDirection="column" gap={1}>
              <TextField
                autoFocus
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment:
                      searchValue !== '' ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchValue('');
                            }}
                            edge="end"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                  },
                }}
                label="Ricerca nome utente"
                // sx={{ maxWidth: 250 }}
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                }}
              />
              <Stack
                sx={{
                  height: 250,
                  border: '1px solid var(--divider-color)',
                  borderRadius: 1,
                }}
              >
                <MenuList sx={{ overflow: 'auto' }}>
                  {getValues().map((item) => (
                    <MenuItem
                      onClick={() => setSessionRef(item.id)}
                      selected={item.id === sessionRef}
                      key={item.id}
                      sx={{
                        py: 1,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'action.selected',
                          '&:hover': {
                            backgroundColor: 'action.selected',
                          },
                        },
                      }}
                    >
                      <Stack flexDirection="column" sx={{ width: '100%' }}>
                        <Typography fontWeight="bold" sx={{ fontSize: 16 }}>
                          {item.userName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {dayjs(item.updatedAt).format('DD/MM/YYYY HH:mm')}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </MenuList>
              </Stack>
            </Stack>
          </Stack>
          <Stack
            flexDirection="row"
            justifyContent="end"
            gap={1}
            sx={{ pt: 2 }}
          >
            <Button
              sx={{
                fontWeight: 400,
                height: 40,
                width: 120,
                mr: '10px',
                justifyContent: 'center',
              }}
              variant="outlined"
              color="primary"
              onClick={onAnnullaClick}
            >
              Annulla
            </Button>
            <Button
              sx={{
                fontWeight: 400,
                height: 40,
                width: 120,
                mr: '10px',
                justifyContent: 'center',
              }}
              disabled={sessionRef === ''}
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              Conferma
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </EmptyPage>
  );
}

export default LoginChooseSessionPage;
